import { mutate } from "swr";
import { useFetcher } from "~/hooks/useFetcher";
import {
  type AccessLevelOption,
  type ShareCollaborators,
  type ShareGroup,
  type ShareUser,
} from "~/pages/manifold-nextgen/components/share/types";
import {
  AgentAccessLevel,
  AgentCollaborator,
  AgentCollaboratorList,
  AgentCollaboratorMember,
  agentSharesPath,
  shareAgent,
  unshareAgent,
} from "~/services/agents";
import {
  EVERYONE_LABEL,
  groupPrincipal,
  isEveryoneId,
  parsePrincipal,
  userPrincipal,
} from "~/services/principals";
import { getOwnerDisplayName } from "~/utils/strings";

// Listed most- to least-permissive: AccessDetailsView ranks a user's effective
// access by this key order when they hold a level both directly and via a group.
const accessLevels: Record<string, AccessLevelOption> = {
  [AgentAccessLevel.OWNER]: {
    label: "Owner",
    description: "Full access, including editing and sharing this agent",
  },
  [AgentAccessLevel.EDITOR]: {
    label: "Editor",
    description: "Can use and edit this agent",
  },
  [AgentAccessLevel.USER]: {
    label: "User",
    description: "Can use this agent",
  },
};

const holderFullName = (holder: AgentCollaborator, email: string): string =>
  getOwnerDisplayName(holder.given_name, holder.family_name, email);

const memberFullName = (member: AgentCollaboratorMember): string =>
  getOwnerDisplayName(member.given_name, member.family_name, member.email);

// The share listing splits into users and groups, each carrying its access level
// as a plain string. Group holders carry the resolved display name and their
// expanded members, who inherit the group's level; user holders carry the
// resolved name and avatar. Enrichment is absent on the share (`POST`) response,
// so each field falls back to the bare email/tag.
export const toAgentCollaborators = (holders: AgentCollaborator[]): ShareCollaborators => {
  const users: ShareUser[] = [];
  const groups: ShareGroup[] = [];
  for (const holder of holders) {
    const { isGroup, id } = parsePrincipal(holder.principal);
    if (isGroup) {
      groups.push({
        name: id,
        display_name: holder.display_name ?? id,
        is_internal: holder.is_internal ?? false,
        access_level: holder.level,
        users: (holder.members ?? []).map((member) => ({
          email: member.email,
          full_name: memberFullName(member),
          image_url: member.image_url ?? undefined,
          access_level: holder.level,
        })),
      });
    } else {
      users.push({
        email: id,
        full_name: isEveryoneId(id) ? EVERYONE_LABEL : holderFullName(holder, id),
        image_url: holder.image_url ?? undefined,
        access_level: holder.level,
      });
    }
  }
  return { users, groups };
};

// Pass `enabled: false` to skip the fetch — e.g. when the caller can't share and
// the collaborator list isn't needed yet.
export const useAgentShare = (agentId: string, enabled = true) => {
  const {
    data,
    isLoading,
    error: collaboratorsError,
  } = useFetcher<AgentCollaboratorList>(enabled ? agentSharesPath(agentId) : null);

  const collaborators = data ? toAgentCollaborators(data.items) : undefined;

  // A principal holds at most one level here, so a level change is a share at the
  // new level followed by an unshare of the old; both endpoints are idempotent.
  // Share first so the principal is never transiently left without access; a
  // principal who momentarily holds both levels is reported at the higher one.
  const handleShare = async (
    recipient: { user?: ShareUser; group?: ShareGroup },
    accessLevel: string | null
  ) => {
    const { user, group } = recipient;
    const principal = user ? userPrincipal(user.email) : group ? groupPrincipal(group.name) : null;
    if (!principal) {
      return;
    }

    const currentLevel =
      (user
        ? collaborators?.users.find((u) => u.email.toLowerCase() === user.email.toLowerCase())
        : collaborators?.groups.find((g) => g.name === group?.name)
      )?.access_level ?? null;

    if (accessLevel === null) {
      if (currentLevel) {
        await unshareAgent(agentId, principal, currentLevel as AgentAccessLevel);
      }
    } else {
      await shareAgent(agentId, principal, accessLevel as AgentAccessLevel);
      if (currentLevel && currentLevel !== accessLevel) {
        await unshareAgent(agentId, principal, currentLevel as AgentAccessLevel);
      }
    }
    await mutate(agentSharesPath(agentId));
  };

  return {
    collaborators,
    isLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel: AgentAccessLevel.USER as string,
  };
};
